import React from "react";
import { colors } from "app/theme";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useAuth0 } from "@auth0/auth0-react";
import { Link, useLocation } from "react-router-dom";
import { PageHeader } from "app/components/page-header";
import { useStoreActions } from "app/state/store/hooks";
import { ReactComponent as LogOutIcon } from "app/assets/vectors/HeaderSignInViewLogOut.svg";
import { ReactComponent as AccountProfileHeaderIcon } from "app/assets/vectors/AccountProfileHeaderIcon.svg";
import { ReactComponent as AccountProfileDeleteIcon } from "app/assets/vectors/AccountProfileDeleteIcon.svg";
import { useCMSData } from "app/hooks/useCMSData";
import { getCMSDataField } from "app/utils/getCMSDataField";

export const AccountProfileLayout: React.FC<{
  children: React.ReactNode;
}> = ({ children }: { children: React.ReactNode }) => {
  const cmsData = useCMSData({ returnData: true });
  const { logout } = useAuth0();
  const location = useLocation();

  const clearStoredUser = useStoreActions((actions) => actions.user.clear);
  const clearIntercomUser = useStoreActions(
    (actions) => actions.IntercomUser.clear
  );
  const clearStripeSubscriptionPlan = useStoreActions(
    (actions) => actions.StripeSubscriptionPlan.clear
  );
  const clearFavourites = useStoreActions(
    (actions) => actions.GetBookmarks.clear
  );

  const handleLogOutButtonClick = () => {
    clearStoredUser();
    clearIntercomUser();
    clearStripeSubscriptionPlan();
    clearFavourites();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const inDeleteAccountView = React.useMemo(() => {
    return location.pathname === "/account-profile/delete";
  }, [location.pathname]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: {
          xs: "20px",
          md: "30px",
          lg: "40px",
        },
      }}
    >
      <PageHeader
        variant="explorer"
        title={getCMSDataField(
          cmsData,
          "pagesAccountProfile.title",
          "Account Profile"
        )}
        subtitle={getCMSDataField(
          cmsData,
          "pagesAccountProfile.subTitle",
          "Manage your account settings and get notifications on your activity alerts."
        )}
        icon={<AccountProfileHeaderIcon />}
      />
      {!inDeleteAccountView && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            "> button, > a": {
              gap: "10px",
              height: "32px",
              display: "flex",
              fontSize: "12px",
              fontWeight: "700",
              borderRadius: "5px",
              alignItems: "center",
              textTransform: "none",
              justifyContent: "center",
              color: colors.primary.white,
              background: colors.secondary.red,
              ":hover": {
                background: colors.secondary.darkRed,
              },
              "> .MuiButton-icon": {
                margin: 0,
              },
            },
          }}
        >
          <Button
            startIcon={<LogOutIcon />}
            onClick={handleLogOutButtonClick}
            sx={{
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
            data-cy="logout-button"
          >
            Sign Out
          </Button>
          <Button
            startIcon={<AccountProfileDeleteIcon />}
            component={Link}
            LinkComponent={Link}
            to="/account-profile/delete"
            sx={{
              width: {
                xs: "100%",
                md: "auto",
              },
            }}
            data-cy="delete-account-button"
          >
            Delete Account
          </Button>
        </Box>
      )}
      {children}
    </Box>
  );
};
